/* Sweet Bar: inline Christmas-card add-on used only inside the Natal gift dialog. */
if (!customElements.get('sweetbar-gift-postcards')) {
  customElements.define('sweetbar-gift-postcards', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.choices = [...this.querySelectorAll('[data-postcard-choice]')];
      this.removeButtons = [...this.querySelectorAll('[data-postcard-remove]')];
      this.qty = this.querySelector('[data-postcard-quantity]');
      this.minus = this.querySelector('[data-postcard-minus]');
      this.plus = this.querySelector('[data-postcard-plus]');
      this.message = this.querySelector('[data-postcard-message]');
      this.counter = this.querySelector('[data-postcard-count]');
      this.add = this.querySelector('[data-postcard-add]');
      this.status = this.querySelector('[data-postcard-status]');
      if (!this.qty || !this.add || !this.status) return;
      this.choices.forEach((choice) => choice.addEventListener('click', () => this.select(choice)));
      this.removeButtons.forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.busy || this.closest('sweetbar-gift-picker')?.dataset.unifiedGift !== 'true') return;
        this.clearSelection();
      }));
      this.minus?.addEventListener('click', () => this.step(-1));
      this.plus?.addEventListener('click', () => this.step(1));
      this.qty.addEventListener('input', () => this.update());
      this.qty.addEventListener('change', () => this.update());
      this.qty.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this.addCard();
        }
      });
      this.message?.addEventListener('input', () => {
        if (this.counter) this.counter.textContent = `${this.message.value.length} / 180`;
        this.message.setCustomValidity(/[<>]|\/uploads\//.test(this.message.value) ? (this.dataset.plainTextError || '') : '');
      });
      this.add.addEventListener('click', () => {
        const parent = this.closest('sweetbar-gift-picker');
        if (parent?.dataset.unifiedGift === 'true') {
          if (!this.selected) return;
          this.status.textContent = '';
          parent.dispatchEvent(new CustomEvent('sweetbar:gift-change', { bubbles: true }));
          return;
        }
        this.addCard();
      });
      const parent = this.closest('sweetbar-gift-picker');
      if (parent?.dataset.unifiedGift === 'true') {
        this.add.querySelector('span').textContent = this.dataset.cardSelected || parent.dataset.cardSelected || '';
      }
      this.update();
    }
    select(choice) {
      if (this.busy || choice.disabled || choice.dataset.available !== 'true') return;
      const changed = this.selected !== choice;
      if (!changed && this.closest('sweetbar-gift-picker')?.dataset.unifiedGift === 'true') {
        this.clearSelection();
        return;
      }
      this.selected = choice;
      this.choices.forEach((item) => {
        item.setAttribute('aria-pressed', String(item === choice));
        const card = item.closest('[data-postcard-card]');
        if (card) card.dataset.selected = String(item === choice);
      });
      this.removeButtons.forEach((button) => {
        button.hidden = button.closest('[data-postcard-card]')?.dataset.selected !== 'true';
      });
      this.qty.min = choice.dataset.min || '1';
      this.qty.step = choice.dataset.step || '1';
      if (changed) this.qty.value = this.qty.min;
      if (choice.dataset.max) this.qty.max = choice.dataset.max;
      else this.qty.removeAttribute('max');
      this.uncertain = false;
      this.status.textContent = '';
      this.update();
    }
    clearSelection() {
      this.selected = null;
      this.choices.forEach((item) => {
        item.setAttribute('aria-pressed', 'false');
        const card = item.closest('[data-postcard-card]');
        if (card) card.dataset.selected = 'false';
      });
      this.removeButtons.forEach((button) => { button.hidden = true; });
      this.status.textContent = '';
      this.update();
    }
    step(direction) {
      if (this.busy || !this.selected) return;
      if (direction > 0) this.qty.stepUp();
      else this.qty.stepDown();
      this.update();
    }
    update() {
      const value = Number(this.qty.value || 1);
      const min = Number(this.qty.min || 1);
      const max = this.qty.max ? Number(this.qty.max) : Infinity;
      const locked = Boolean(this.busy);
      if (this.minus) this.minus.disabled = locked || !this.selected || value <= min;
      if (this.plus) this.plus.disabled = locked || !this.selected || value >= max;
      this.choices.forEach((choice) => {
        choice.disabled = locked || choice.dataset.available !== 'true';
      });
      this.qty.disabled = locked;
      if (this.message) this.message.disabled = locked;
      this.add.disabled = locked || Boolean(this.uncertain) || !this.selected || this.selected.dataset.available !== 'true';
      this.add.setAttribute('aria-busy', String(locked));
    }
    async addCard() {
      if (this.add.disabled || this.busy || !this.selected) return;
      const id = Number(this.selected.dataset.variantId);
      const quantity = Number(this.qty.value);
      const message = (this.message?.value || '').trim();
      if (!this.qty.reportValidity() || !this.message?.reportValidity() || !Number.isSafeInteger(id) || id < 1 || !Number.isSafeInteger(quantity) || quantity < 1 || message.length > 180 || /[<>]|\/uploads\//.test(message)) return;
      const properties = { _sb_natal_parent_product_id: this.dataset.parentId || '' };
      if (this.dataset.parentTitle && this.dataset.forProperty) properties[this.dataset.forProperty] = this.dataset.parentTitle;
      if (message && this.dataset.messageProperty) properties[this.dataset.messageProperty] = message;
      this.busy = true;
      this.status.textContent = '';
      this.update();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id, quantity, properties })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.uncertain = response.status >= 500;
          const detail = typeof result.description === 'string' ? result.description : this.dataset.error;
          this.status.textContent = detail + (this.uncertain ? ' ' + this.dataset.check : '');
          return;
        }
        this.uncertain = false;
        this.status.textContent = this.dataset.success;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sweetbar-gift-postcards', productVariantId: String(id), cartData: result });
        }
        if (this.message) {
          this.message.value = '';
          this.message.setCustomValidity('');
        }
        if (this.counter) this.counter.textContent = '0 / 180';
      } catch (error) {
        this.uncertain = true;
        this.status.textContent = this.dataset.error + ' ' + this.dataset.check;
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.update();
      }
    }
  });
}
